(() => {
  'use strict';

  if (window.DeebHumidorDisclosures) return;

  const ROOT_SELECTOR = 'product-info[data-product-family="humidor"]';
  const DETAILS_SELECTOR = '.deeb-humidor-disclosure';
  const MOBILE_QUERY = '(max-width: 749px)';

  class HumidorDisclosureController {
    constructor(root) {
      this.root = root;
      this.details = [...root.querySelectorAll(DETAILS_SELECTOR)];
      this.mediaQuery = window.matchMedia(MOBILE_QUERY);
      this.mobileStates = new Map(this.details.map((details) => [details, false]));
      this.syncing = false;
      this.destroyed = false;

      if (!this.ready) return;

      this.details.forEach((details) => {
        details.addEventListener('toggle', this.handleToggle);
      });

      if (typeof this.mediaQuery.addEventListener === 'function') {
        this.mediaQuery.addEventListener('change', this.handleViewportChange);
      } else {
        this.mediaQuery.addListener(this.handleViewportChange);
      }

      this.syncToViewport();
    }

    get ready() {
      return this.details.length > 0;
    }

    get isMobile() {
      return this.mediaQuery.matches;
    }

    setOpen(details, open) {
      if (details.open === open) return;
      details.open = open;
    }

    syncToViewport() {
      if (!this.ready || this.destroyed) return;

      this.syncing = true;

      if (this.isMobile) {
        this.details.forEach((details) => {
          this.setOpen(details, this.mobileStates.get(details) === true);
        });
      } else {
        this.details.forEach((details) => this.setOpen(details, true));
      }

      queueMicrotask(() => {
        this.syncing = false;
      });
    }

    handleToggle = (event) => {
      if (this.syncing || !this.isMobile) return;
      const details = event.currentTarget;
      this.mobileStates.set(details, details.open);
    };

    handleViewportChange = () => {
      this.syncToViewport();
    };

    destroy() {
      if (this.destroyed) return;
      this.destroyed = true;

      this.details.forEach((details) => {
        details.removeEventListener('toggle', this.handleToggle);
      });

      if (typeof this.mediaQuery.removeEventListener === 'function') {
        this.mediaQuery.removeEventListener('change', this.handleViewportChange);
      } else {
        this.mediaQuery.removeListener(this.handleViewportChange);
      }
    }
  }

  const instances = new Map();

  const initializeRoot = (root) => {
    if (!(root instanceof HTMLElement) || !root.matches(ROOT_SELECTOR)) return null;
    if (instances.has(root)) return instances.get(root);

    const instance = new HumidorDisclosureController(root);
    if (!instance.ready) return null;

    instances.set(root, instance);
    return instance;
  };

  const initializeAll = (scope = document) => {
    if (scope instanceof HTMLElement && scope.matches(ROOT_SELECTOR)) initializeRoot(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(initializeRoot);
  };

  const destroyRoot = (root) => {
    const instance = instances.get(root);
    if (!instance) return;
    instance.destroy();
    instances.delete(root);
  };

  const destroyWithin = (scope) => {
    instances.forEach((instance, root) => {
      if (!root.isConnected || root === scope || scope?.contains?.(root)) {
        instance.destroy();
        instances.delete(root);
      }
    });
  };

  document.addEventListener('product-info:loaded', (event) => {
    destroyRoot(event.target);
    initializeRoot(event.target);
  });

  document.addEventListener('shopify:section:load', (event) => {
    destroyWithin(event.target);
    initializeAll(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    destroyWithin(event.target);
  });

  window.addEventListener('pageshow', () => {
    instances.forEach((instance) => instance.syncToViewport());
    initializeAll();
  });

  initializeAll();

  window.DeebHumidorDisclosures = Object.freeze({
    get activeInstances() {
      return instances.size;
    },
    sync() {
      instances.forEach((instance) => instance.syncToViewport());
    },
  });
})();
